import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import OverviewIcon from '../assets/icons/overview-home.svg';
import FormsIcon from '../assets/icons/forms.svg';
import logo from '../assets/logos/cad-icon-white.svg';
import asignIconwhite from '../assets/logos/asign-icon-white.svg';
import navIcon from '../assets/icons/arrow.svg'
import '../styling/layout.css';


function Navbar({ sidebarOpen, setSidebarOpen, videoSidebarOpen, onVideoSidebarToggle }) {
  const { t, i18n } = useTranslation();

  const toggleSidebar = () => {
    setSidebarOpen(!sidebarOpen);
  };

  const navigate = useNavigate();
  const location = useLocation();

  // Close the nav sidebar on page change for mobile only; desktop keeps it open
  useEffect(() => {
    if (window.innerWidth <= 1089) {
      setSidebarOpen(false);
    }
  }, [location.pathname, setSidebarOpen]);

  const EN_TO_FR = {
    '/': '/fr',
    '/forms': '/fr/formulaires',
    '/forms/disability-tax-credit': '/fr/formulaires/credit-impot-pour-personnes-handicapees',
    '/forms/canada-disability-benefit': '/fr/formulaires/prestation-canadienne-pour-les-personnes-handicapees',
  };
  const FR_TO_EN = {
    '/fr': '/',
    '/fr/formulaires': '/forms',
    '/fr/formulaires/credit-impot-pour-personnes-handicapees': '/forms/disability-tax-credit',
    '/fr/formulaires/prestation-canadienne-pour-les-personnes-handicapees': '/forms/canada-disability-benefit',
  };

  // ----- Mobile swipe handlers for the nav sidebar -----
  // Swipe right from the left edge = open. Swipe left on the open sidebar = close.
  const SWIPE_THRESHOLD_PX = 40;
  const swipeRef = useRef({ startX: 0, currentX: 0, active: false });
  const isMobile = () => window.innerWidth <= 1089;

  const handleEdgeTouchStart = (e) => {
    if (!isMobile()) return;
    swipeRef.current.startX = e.touches[0].clientX;
    swipeRef.current.currentX = e.touches[0].clientX;
    swipeRef.current.active = true;
  };
  const handleEdgeTouchMove = (e) => {
    if (!swipeRef.current.active) return;
    swipeRef.current.currentX = e.touches[0].clientX;
  };
  const handleEdgeTouchEnd = () => {
    if (!swipeRef.current.active) return;
    const delta = swipeRef.current.currentX - swipeRef.current.startX;
    swipeRef.current.active = false;
    if (delta > SWIPE_THRESHOLD_PX) setSidebarOpen(true);
  };

  const handleSidebarTouchStart = (e) => {
    if (!isMobile()) return;
    swipeRef.current.startX = e.touches[0].clientX;
    swipeRef.current.currentX = e.touches[0].clientX;
    swipeRef.current.active = true;
  };
  const handleSidebarTouchMove = (e) => {
    if (!swipeRef.current.active) return;
    swipeRef.current.currentX = e.touches[0].clientX;
  };
  const handleSidebarTouchEnd = () => {
    if (!swipeRef.current.active) return;
    const delta = swipeRef.current.currentX - swipeRef.current.startX;
    swipeRef.current.active = false;
    if (delta < -SWIPE_THRESHOLD_PX) setSidebarOpen(false);
  };

  const isFR = i18n.language === 'fr';
  const overviewPath = isFR ? '/fr' : '/';
  const formsPath = isFR ? '/fr/formulaires' : '/forms';
  const isOverview = location.pathname === '/' || location.pathname === '/fr';
  const isForms = location.pathname === '/forms' || location.pathname === '/fr/formulaires';
  const isDTC = location.pathname === '/forms/disability-tax-credit' || location.pathname === '/fr/formulaires/credit-impot-pour-personnes-handicapees';
  const isCDB = location.pathname === '/forms/canada-disability-benefit' || location.pathname === '/fr/formulaires/prestation-canadienne-pour-les-personnes-handicapees';

 

  return (
    <>
      <div className={`curve-behind ${sidebarOpen ? 'sidebar-open' : ''} ${videoSidebarOpen ? 'video-sidebar-open' : ''}`}>
      <div className={`curve ${sidebarOpen ? 'sidebar-open' : ''} ${videoSidebarOpen ? 'video-sidebar-open' : ''}`}></div>
      </div>
      {/* Top Navigation Bar */}
      
      <nav className="navbar bg-primary sticky-top align-items-center max-h-[70px]">
        <div className="container-fluid d-flex justify-content-between align-items-center">

          {/* Mobile-only hamburger button (hidden on desktop via CSS).
              Wrapper exists so the left side can flex to match the right
              (for centered brand) without inflating the button's hit area. */}
          <div className="mobile-sidebar-toggle-wrapper">
            <button className={`mobile-sidebar-toggle ${sidebarOpen ? 'open' : ''}`} onClick={toggleSidebar} aria-label="Toggle sidebar">
              <span></span>
              <span></span>
              <span></span>
            </button>
          </div>

          <Link className="navbar-brand" to={overviewPath}>
            <img src={logo} alt="CAD Logo" className="navbar-logo" />
            <span className="h6 text-white">{t('navbar.logo')}</span>
          </Link>

           

          <div className="d-flex align-items-center">

            <div className="CAD-ASC-label">
              <p className="mb-0 h6 text-white"><a href="https://www.cad-asc.ca" target="_blank" rel="noopener noreferrer" style={{ color: 'white', textDecoration: 'none' }}>{t('navbar.brand')}</a></p>
            </div>
          

            <div className="divider"></div>
          {/* LANGUAGE TOGGLE */}

            <div className="lang-toggle-container">
              <button 
                className={`lang-toggle ${i18n.language === 'en' ? 'active' : ''}`}
                onClick={() => {
                  i18n.changeLanguage('en');
                  navigate(FR_TO_EN[location.pathname] || '/');
                }}>
                EN
              </button>

              <button 
                className={`lang-toggle ${i18n.language === 'fr' ? 'active' : ''}`}
                onClick={() => {
                  i18n.changeLanguage('fr');
                  navigate(EN_TO_FR[location.pathname] || '/fr');
                }}>
                FR
              </button>

            </div>
          </div> 

        </div>
        
      </nav>

      {/* BREADCRUMB NAVIGATION */}

      <div className={`breadcrumb-nav-bg ${sidebarOpen ? 'sidebar-open' : ''} ${videoSidebarOpen ? 'video-sidebar-open' : ''}`} >
      <nav className={`breadcrumb-nav ${sidebarOpen ? 'sidebar-open' : ''} ${videoSidebarOpen ? 'video-sidebar-open' : ''}`} aria-label="breadcrumb" style={{"--bs-breadcrumb-divider": "'›'"}} >
        <ol className="breadcrumb">
          {(isDTC || isCDB) && (
            <li className="breadcrumb-item"><Link to={formsPath}>{t('navbar.forms')}</Link></li>
          )}
          {isOverview && (
            <li className="breadcrumb-item active"><Link to={overviewPath}>{t('navbar.overview')}</Link></li>
          )}
          {isForms && (
            <li className="breadcrumb-item active"><Link to={formsPath}>{t('navbar.forms')}</Link></li>
          )}
          {isDTC && (
            <li className="breadcrumb-item active">{t('breadcrumb.dtc')}</li>
          )}
          {isCDB && (
            <li className="breadcrumb-item active">{t('breadcrumb.cdb')}</li>
          )}
        </ol>
</nav>
</div>

      {/* MOBILE EDGE SWIPE ZONE — invisible strip on the left edge; swipe right to open */}
      {!sidebarOpen && (
        <div
          className="sidebar-edge-swipe-zone"
          onTouchStart={handleEdgeTouchStart}
          onTouchMove={handleEdgeTouchMove}
          onTouchEnd={handleEdgeTouchEnd}
        />
      )}

      {/* SIDEBAR */}

      <aside
        className={`sidebar ${sidebarOpen ? 'open' : 'collapsed'}`}
        onTouchStart={handleSidebarTouchStart}
        onTouchMove={handleSidebarTouchMove}
        onTouchEnd={handleSidebarTouchEnd}
      >


        {/* SIDEBAR NAV LINKS */}

        <nav className="sidebar-nav">
          <Link to={overviewPath} className={`sidebar-link ${isOverview ? 'active' : ''}`}>
            <img src={OverviewIcon} alt="Overview" className="sidebar-icon" />
            <span className="sidebar-label">{t('navbar.overview')}</span>
          </Link>

          <Link to={formsPath} className={`sidebar-link ${isForms || isDTC || isCDB ? 'active' : ''}`}>
            <img src={FormsIcon} alt="Forms" className="sidebar-icon" />
            <span className="sidebar-label">{t('navbar.forms')}</span>
          </Link>
        </nav>

        {/* SIDEBAR TOGGLE BUTTON */}

        <div className={`sidebar-footer ${sidebarOpen ? 'open' : ''}`}>
          <div className={`sidebar-toggle-bg ${sidebarOpen ? 'open' : ''}`} onClick={toggleSidebar}>
            <span className="sidebar-toggle-text">{sidebarOpen ? t('navbar.nav-toggle') : ''}</span>
          </div>
          <button className={`sidebar-toggle-btn ${sidebarOpen ? 'open' : ''}`} onClick={toggleSidebar} aria-label="Toggle sidebar">
            <img src={navIcon} alt="" className={`sidebar-toggle-icon ${sidebarOpen ? 'rotated' : ''}`} />
          </button>
        </div>
      </aside>

      {/*RESPONSIVE SIDEBAR OVERLAY*/}
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={toggleSidebar}></div>
      )}

      {/* VIDEO SIDEBAR TOGGLE TAB */}
      <div className={`video-sidebar-tab ${videoSidebarOpen ? 'open' : ''}`} onClick={onVideoSidebarToggle} title="Open video">

        { /* ICON GO HERE */ }
        <img src={asignIconwhite} alt="ASL Icon" className="video-sidebar-tab-icon" />
      </div>
    </>
  );
};

export default Navbar;
